// pages/ltv/LtvDecomposedView.jsx — SOUND-811 (LTV v2: decomposed model, internal)
//
// Net LTV per acquired subscription at 3 / 6 / 12 months, from the v2 cohort
// spine (GET /ltv/v2/cohorts, over MART_LTV_COHORT_V2), computed client-side by
// src/lib/ltvDecomposed.js. Everything shown is MEASURED from the mart
// or PROJECTED by the model, and labelled as such. The cost/CAC inputs were
// removed (SOUND-837, CPO 2026-10-28); decomposedEconomics stays in lib.
//
// Sections:
//   • headline net LTV at 3 / 6 / 12 months, split measured vs. projected
//   • survival by cycle (measured vs. projected)
//   • "why customers leave": each cycle's eligible split into renewed first
//     try, recovered after a decline, payment failed, never attempted
//   • revenue lines per acquired subscription (subscription, refunds,
//     chargebacks, add-ons, upsells)
//   • per cohort: net LTV, first renewal vs. the last 3 cohorts, the "no
//     trace" jump flag (gap N2), and an "indicative" label until the cohort's
//     first renewal is complete
import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ReferenceArea, ResponsiveContainer,
} from "recharts";
import clsx from "clsx";
import { computeDecomposedLtv } from "lib/ltvDecomposed";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth, requireAuth } from "utilities/auth";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";
import PageHeader from "components/PageLayout";
import PageKpis from "components/PageKpis";
import Skeleton from "components/Skeleton";
import {
  LineChart as LineChartIcon, AlertTriangle, Database, Target,
  Wallet, CalendarClock, Repeat,
} from "lucide-react";
import {
  ink, cssVar, tooltipStyle, fmtMoney, fmtMoney2, fmtPct1, fmtNum, EYEBROW, CARD, TH, TD, TR, SEG, segOption,
  MeasuredBadge, Pill, Panel, Key, SelectField,
} from "./ltvUi";

const HORIZON_LABEL = { 3: "3-month", 6: "6-month", 12: "12-month" };

// Pick USD when the client bills in it, else its first currency. Dollars from
// different currencies can't be added, so the view always shows one.
const defaultCurrency = (opts) => (opts.includes("USD") ? "USD" : opts[0] || "");

function FlagPill({ tone, children, title }) {
  return <span title={title}><Pill tone={tone}>{children}</Pill></span>;
}

function LtvDecomposedView() {
  const { user } = useAuth();
  const effectiveCompanyId = useEffectiveCompanyId();
  const auth = useMemo(() => ({ id: user?.id, accessToken: user?.accessToken }), [user]);

  const [companies, setCompanies] = useState([]);
  const [productOpts, setProductOpts] = useState([]);
  const [familyOpts, setFamilyOpts] = useState([]);
  const [currencyOpts, setCurrencyOpts] = useState([]);
  const [company, setCompany] = useState("");
  const [groupBy, setGroupBy] = useState("product"); // "product" | "family"
  const [product, setProduct] = useState("");
  const [family, setFamily] = useState("");
  const [currency, setCurrency] = useState("");
  const [basis, setBasis] = useState("new"); // "new" = charged − tax; "orig" = whole charge

  const [rows, setRows] = useState([]);
  const [clientRows, setClientRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  // Companies once.
  useEffect(() => {
    if (!user || !effectiveCompanyId) return;
    (async () => {
      try {
        const res = await makeApiRequest("get", apiRoutes.ltvV2Filters(), auth, effectiveCompanyId);
        const f = res?.data || {};
        setCompanies(f.companies || []);
        if (f.companies?.length) setCompany((c) => c || f.companies[0]);
      } catch { /* the page wrapper already checked availability */ }
    })();
  }, [user, effectiveCompanyId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Products, families and currencies for the selected company.
  useEffect(() => {
    if (!user || !effectiveCompanyId || !company) return;
    (async () => {
      try {
        const res = await makeApiRequest("get", apiRoutes.ltvV2Filters(company), auth, effectiveCompanyId);
        const f = res?.data || {};
        const products = f.products || [];
        const families = f.offerFamilies || [];
        const currencies = f.currencies || [];
        setProductOpts(products);
        setFamilyOpts(families);
        setCurrencyOpts(currencies);
        setProduct((p) => (products.includes(p) ? p : products[0] || ""));
        setFamily((x) => (families.includes(x) ? x : families[0] || ""));
        setCurrency((c) => (currencies.includes(c) ? c : defaultCurrency(currencies)));
      } catch { /* non-fatal */ }
    })();
  }, [user, effectiveCompanyId, company, auth]);

  const selection = groupBy === "family" ? { family } : { product };
  const selected = groupBy === "family" ? family : product;

  const loadRows = useCallback(async () => {
    if (!user || !effectiveCompanyId || !company || !selected) { setRows([]); setClientRows([]); return; }
    setLoading(true); setError(false);
    try {
      const res = await makeApiRequest("get", apiRoutes.ltvV2Cohorts({ company, ...selection, currency }), auth, effectiveCompanyId);
      setRows(res?.data?.data || []);
      setClientRows(res?.data?.client || []);
    } catch { setError(true); setRows([]); setClientRows([]); } finally { setLoading(false); }
  }, [user, effectiveCompanyId, company, selected, groupBy, currency, auth]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadRows(); }, [loadRows]);

  const model = useMemo(() => computeDecomposedLtv(rows, { clientRows, basis }), [rows, clientRows, basis]);
  const hasData = rows.length > 0 && model.horizons[3].K > 0;
  const h6 = model.horizons[6];
  const clientDataEnd = rows[0]?.client_data_end || null;
  const mixedCurrency = !currency && currencyOpts.length > 1;

  // ── chart data ──
  const survival = useMemo(() => {
    const pts = model.horizons[12].survival || [];
    const lastObs = model.lastObservedCycle;
    return pts.map((p) => ({
      cycle: p.cycle,
      measured: p.cycle <= lastObs ? +(p.survival * 100).toFixed(2) : null,
      // Overlap one point so the two lines join.
      projected: p.cycle >= lastObs ? +(p.survival * 100).toFixed(2) : null,
    }));
  }, [model]);
  const projStart = model.lastObservedCycle + 1;
  const projEnd = model.horizons[12].K;

  const leave = useMemo(() => model.leave.map((l) => ({
    cycle: l.cycle,
    firstTry: +(l.renewedFirstTry * 100).toFixed(2),
    recovered: +(l.renewedAfterDecline * 100).toFixed(2),
    paymentFailed: +(l.lostPaymentFailed * 100).toFixed(2),
    noAttempt: +(l.lostNoAttempt * 100).toFixed(2),
    eligible: l.eligible,
  })), [model.leave]);


  const grid = cssVar("--border", 0.55);
  const rl = model.revenueLines;

  return (
    <div>
      <PageHeader
        title="LTV"
        subtitle="Net revenue per acquired subscription at 3, 6 and 12 months: measured where cohorts have paid, projected by the decomposed model (why customers leave × payment success) beyond that."
        badge="Internal · Decomposed (v2)"
        meta={[
          { label: "Scope", value: company ? `${company}${selected ? ` · ${selected}` : ""}${currency ? ` · ${currency}` : ""}` : "—" },
          { label: "Cohorts", value: hasData ? fmtNum(model.cohorts.length, 0) : "—" },
          { label: "Cadence", value: hasData ? `${fmtNum(model.cadenceDays, 0)} days` : "—" },
          { label: "Data through", value: clientDataEnd || "—" },
        ]}
      />

      <div className="container py-6 space-y-5">
        {/* ── Scope bar ── */}
        <div className={clsx(CARD, "p-4")}>
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <MeasuredBadge />
            <span className="text-xs text-muted-foreground">Counts and dollars from the v2 LTV mart. Only cycles whose outcome is known (due date + 30-day retry buffer) are counted.</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 items-end">
            <SelectField label="Client" value={company} onChange={setCompany}>
              {companies.length === 0 && <option value="">No clients</option>}
              {companies.map((c) => <option key={c} value={c}>{c}</option>)}
            </SelectField>
            <SelectField label="Group by" value={groupBy} onChange={setGroupBy}>
              <option value="product">Product</option>
              <option value="family">Offer family</option>
            </SelectField>
            {groupBy === "family" ? (
              <SelectField label="Offer family" value={family} onChange={setFamily}>
                {familyOpts.length === 0 && <option value="">No families</option>}
                {familyOpts.map((x) => <option key={x} value={x}>{x}</option>)}
              </SelectField>
            ) : (
              <SelectField label="Product" value={product} onChange={setProduct}>
                {productOpts.length === 0 && <option value="">No products</option>}
                {productOpts.map((p) => <option key={p} value={p}>{p}</option>)}
              </SelectField>
            )}
            <SelectField label="Currency" value={currency} onChange={setCurrency}>
              <option value="">All (mixed)</option>
              {currencyOpts.map((c) => <option key={c} value={c}>{c}</option>)}
            </SelectField>
            <div className="min-w-0">
              <label className={clsx(EYEBROW, "block mb-1")}>Revenue</label>
              <div className={SEG}>
                {[["new", "Charged − tax"], ["orig", "Original"]].map(([b, label]) => (
                  <button key={b} onClick={() => setBasis(b)}
                    title={b === "new" ? "Amount charged minus tax, split by item line (proposal Section 7)" : "Whole charged amount, as the current method uses (for reconciliation)"}
                    aria-pressed={basis === b}
                    className={clsx("px-3 py-2 text-sm", segOption(basis === b))}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          {(mixedCurrency || (hasData && model.warnings.length > 0)) && (
            <div className="mt-3 flex items-start gap-2 text-xs text-warning">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <div className="space-y-0.5">
                {mixedCurrency && <div>Several currencies are mixed: dollar amounts add different currencies together. Pick one currency.</div>}
                {hasData && model.warnings.map((w, i) => <div key={i}>{w}</div>)}
              </div>
            </div>
          )}
        </div>

        {error && <div className={clsx(CARD, "p-8 text-center text-sm text-destructive")}>Failed to load the cohort spine.</div>}

        {loading && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className={clsx(CARD, "p-5")}><Skeleton.Rect className="h-8 w-24" /><Skeleton.Rect className="h-3 w-16 mt-3" /></div>
            ))}
          </div>
        )}

        {!loading && !hasData && !error && (
          <div className={clsx(CARD, "p-12 text-center")}>
            <LineChartIcon className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">Pick a client and product to see its LTV.</p>
          </div>
        )}

        {!loading && hasData && (
          <>
            {/* ── Headline: net LTV at 3 / 6 / 12 months ── */}
            <PageKpis
              columns={{ base: 2, md: 4, lg: 4 }}
              items={[
                {
                  key: "h6", label: "6-month net LTV", value: fmtMoney(h6.ltv), format: "text", tone: "brand", icon: <Wallet />,
                  hint: <>{fmtMoney(h6.measured)} measured · {fmtMoney(h6.projected)} projected · {h6.K} cycles</>,
                },
                ...[3, 12].map((H) => {
                  const h = model.horizons[H];
                  return {
                    key: `h${H}`, label: `${HORIZON_LABEL[H]} net LTV${H === 12 ? " · projected" : ""}`, value: fmtMoney(h.ltv),
                    format: "text", tone: "neutral", icon: <CalendarClock />,
                    hint: <>{fmtMoney(h.measured)} measured · {fmtMoney(h.projected)} projected · {h.K} cycles</>,
                  };
                }),
                {
                  key: "r2", label: "First renewal", value: fmtPct1(model.curve?.renewal?.[2]), format: "text", tone: "neutral", icon: <Repeat />,
                  hint: model.usedClientCurve
                    ? <Pill tone="warn">client curve</Pill>
                    : <>{fmtNum(model.firstRenewals, 0)} first renewals{model.sbg ? ` · sBG α ${fmtNum(model.sbg.alpha, 2)} β ${fmtNum(model.sbg.beta, 2)}` : ""}</>,
                },
              ]}
            />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {/* ── Survival ── */}
              <Panel
                title="Still paying, by cycle"
                question="Share of acquired subscriptions still paying at each cycle, out to 12 months."
                legend={<>
                  <Key color={ink("primary")}>Measured</Key>
                  <Key color={ink("secondary")} dash>Projected</Key>
                </>}
              >
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={survival} margin={{ top: 6, right: 14, bottom: 4, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={grid} />
                    <XAxis dataKey="cycle" tick={{ fontSize: 11 }} tickFormatter={(v) => `C${v}`} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} width={42} tickFormatter={(v) => `${v}%`} />
                    <Tooltip contentStyle={tooltipStyle()}
                      formatter={(v, n) => [v == null ? "—" : `${Number(v).toFixed(1)}%`, n === "measured" ? "Measured" : "Projected"]}
                      labelFormatter={(l) => `Cycle ${l}`} />
                    {projStart <= projEnd && (
                      <ReferenceArea x1={projStart} x2={projEnd} fill={cssVar("--muted-foreground", 0.07)} strokeOpacity={0} />
                    )}
                    <Line name="measured" type="monotone" dataKey="measured" stroke={ink("primary")} strokeWidth={2.6}
                      dot={{ r: 2.5, fill: ink("primary") }} isAnimationActive={false} connectNulls={false} />
                    <Line name="projected" type="monotone" dataKey="projected" stroke={ink("secondary")} strokeWidth={2.4}
                      strokeDasharray="6 5" dot={false} isAnimationActive={false} connectNulls={false} />
                  </LineChart>
                </ResponsiveContainer>
              </Panel>

              {/* ── Why customers leave ── */}
              <Panel
                title="Why customers leave"
                question="Each cycle's eligible subscriptions: renewed, recovered after a decline, payment failed, or never attempted (read as cancelled)."
                legend={<>
                  <Key color={ink("primary")}>Renewed</Key>
                  <Key color={ink("primary", 0.45)}>Recovered</Key>
                  <Key color={ink("warning")}>Payment failed</Key>
                  <Key color={ink("destructive")}>Never attempted</Key>
                </>}
              >
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={leave} margin={{ top: 6, right: 14, bottom: 4, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={grid} vertical={false} />
                    <XAxis dataKey="cycle" tick={{ fontSize: 11 }} tickFormatter={(v) => `C${v}`} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} width={42} tickFormatter={(v) => `${v}%`} />
                    <Tooltip contentStyle={tooltipStyle()} labelFormatter={(l) => `Cycle ${l}`}
                      formatter={(v, n) => [`${Number(v).toFixed(1)}%`, {
                        firstTry: "Renewed first try", recovered: "Recovered after decline",
                        paymentFailed: "Payment failed", noAttempt: "Never attempted",
                      }[n]]} />
                    <Bar dataKey="firstTry" stackId="s" fill={ink("primary")} isAnimationActive={false} />
                    <Bar dataKey="recovered" stackId="s" fill={ink("primary", 0.45)} isAnimationActive={false} />
                    <Bar dataKey="paymentFailed" stackId="s" fill={ink("warning")} isAnimationActive={false} />
                    <Bar dataKey="noAttempt" stackId="s" fill={ink("destructive")} isAnimationActive={false} />
                  </BarChart>
                </ResponsiveContainer>
              </Panel>
            </div>

            {/* ── Revenue lines ── */}
            {rl && (
              <Panel
                title="Revenue lines, life to date"
                question={`Per acquired subscription (${fmtNum(rl.acquired, 0)} acquired). Add-ons and upsells are attached to the customer's first subscription while it's still paying.`}
              >
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
                  {[
                    ["Subscription", rl.subscription],
                    ["Refunds", rl.refunds],
                    ["Chargebacks", rl.chargebacks],
                    ["Add-ons", rl.addons],
                    ["Upsells", rl.upsells],
                    ["Add-on / upsell refunds + CBs", rl.otherRefundsChargebacks],
                    ["Net", rl.net],
                  ].map(([label, v]) => (
                    <div key={label} className="min-w-0">
                      <div className={EYEBROW}>{label}</div>
                      <div className={clsx("mt-1 text-lg font-semibold tabular-nums", v < 0 && "text-destructive")}>{fmtMoney2(v)}</div>
                    </div>
                  ))}
                </div>
              </Panel>
            )}

            {/* ── Per cohort ── */}
            <Panel
              title="By cohort"
              question="Each signup month's net LTV: its own actuals for every cycle the whole cohort has reached, the model beyond that. First renewal is compared with the previous 3 cohorts, pooled."
              legend={<span className="text-[11px] text-muted-foreground">"Indicative" = the cohort's first renewal isn't complete yet, so its number rests on the pooled curve.</span>}
            >
              <div className="overflow-x-auto -mx-1">
                <table className="w-full text-sm">
                  <thead className="bg-card">
                    <tr>
                      <th className={TH}>Cohort</th>
                      <th className={clsx(TH, "text-right")}>Subs</th>
                      <th className={clsx(TH, "text-right")}>3-mo</th>
                      <th className={clsx(TH, "text-right")}>6-mo</th>
                      <th className={clsx(TH, "text-right")}>12-mo</th>
                      <th className={clsx(TH, "text-right")}>Actuals to</th>
                      <th className={clsx(TH, "text-right")}>First renewal</th>
                      <th className={clsx(TH, "text-right")}>Last 3 cohorts</th>
                      <th className={TH}>Flags</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {[...model.cohorts].reverse().map((c) => {
                      const delta = c.firstRenewal != null && c.baselineFirstRenewal != null ? c.firstRenewal - c.baselineFirstRenewal : null;
                      return (
                        <tr key={c.cohortMonth} className={clsx(TR, c.indicative && "text-muted-foreground")}>
                          <td className={clsx(TD, "font-medium tabular-nums")}>{String(c.cohortMonth).slice(0, 7)}</td>
                          <td className={clsx(TD, "text-right tabular-nums")}>{fmtNum(c.size, 0)}</td>
                          {[3, 6, 12].map((H) => (
                            <td key={H} className={clsx(TD, "text-right tabular-nums")}
                              title={`${fmtMoney2(c.horizons[H].measured)} measured + ${fmtMoney2(c.horizons[H].projected)} projected`}>
                              {fmtMoney(c.horizons[H].ltv)}
                            </td>
                          ))}
                          <td className={clsx(TD, "text-right tabular-nums")}>C{c.lastCompleteCycle}</td>
                          <td className={clsx(TD, "text-right tabular-nums")}>
                            {fmtPct1(c.firstRenewal)}
                            {delta != null && Math.abs(delta) >= 0.05 && (
                              <span className={clsx("ml-1.5 text-[11px] font-semibold", delta < 0 ? "text-destructive" : "text-success")}>
                                {delta > 0 ? "+" : ""}{(delta * 100).toFixed(0)} pts
                              </span>
                            )}
                          </td>
                          <td className={clsx(TD, "text-right tabular-nums")}>{fmtPct1(c.baselineFirstRenewal)}</td>
                          <td className={TD}>
                            <div className="flex gap-1.5 flex-wrap">
                              {c.indicative && (
                                <FlagPill tone="muted" title="The cohort's first renewal isn't complete yet.">
                                  <Target className="h-3 w-3" /> indicative
                                </FlagPill>
                              )}
                              {c.noTraceJump && (
                                <FlagPill tone="down"
                                  title={`Never-attempted share at the first renewal is ${fmtPct1(c.noTraceShare)} vs ${fmtPct1(c.baselineNoTraceShare)} for the previous 3 cohorts. Check for a billing hold or MID cap before reading it as cancellations.`}>
                                  <AlertTriangle className="h-3 w-3" /> no-trace jump
                                </FlagPill>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Panel>

            <p className={clsx("pt-1 flex items-center gap-1.5", EYEBROW)}>
              <Database className="h-3 w-3" />
              From MART_LTV_COHORT_V2 · decomposed model (SOUND-808) · net of refunds and chargebacks · no discounting · CheckoutChamp only
            </p>
          </>
        )}
      </div>
    </div>
  );
}

export default requireAuth(LtvDecomposedView);
